import React, { useEffect, useMemo, useState } from "react";

import Preloader from "~/components/Preloader";
import useCourseLevelsSelectOptions from "~/entity/Courses/hooks/useCourseLevelsSelectOptions";
import { CoursesResponse, useCourses } from "~/entity/Courses";
import { useGetLocationData } from "~/hooks/locationData";
import { defaultValues } from "~/pages/Editor/EditorCatalogEducationalPrograms/EditorCourses/CoursesManagement/formFields";
import { CoursesManagement } from "~/pages/Editor/EditorCatalogEducationalPrograms/EditorCourses/CoursesManagement/index";
import { optionsStatus } from "~/pages/Editor/EditorCatalogEducationalPrograms/options";
import { OpenModal } from "~/pages/Editor/type";

interface ICoursesManagementHocProps {
  formId: string;
  refetch: () => void;
  closeModal: () => void;
  isLoading?: boolean;
}

export const CoursesManagementHoc = (props: ICoursesManagementHocProps) => {
  const [currentCourse, setCurrentCourse] = useState<CoursesResponse | null>(
    null,
  );
  const [getCoursesLoading, setGetCoursesLoading] = useState<boolean>(true);
  const { coursesGetSingle } = useCourses();
  const { openModal, currentId } = useGetLocationData();
  const { levelsSelectOptions: levelSelectOptionsList } =
    useCourseLevelsSelectOptions();

  const toSelectOptions = (
    list: { instanceId: number; name: string; codeIS: string }[],
  ) =>
    list.map(({ instanceId, name, codeIS }) => ({
      value: String(instanceId),
      label: `${codeIS} ${name}`,
    }));

  useEffect(() => {
    if (openModal === OpenModal.Edit && currentId) {
      setGetCoursesLoading(true);
      coursesGetSingle(currentId)
        .then((res) => setCurrentCourse(res.data?.course ?? null))
        .finally(() => setGetCoursesLoading(false));
    }
  }, [currentId]);

  const formInitialValues = useMemo(() => {
    if (openModal !== OpenModal.Edit || !currentCourse) return defaultValues;
    if (levelSelectOptionsList.length === 0) return defaultValues;
    const levelOption = levelSelectOptionsList.find(
      (opt) => opt.value === currentCourse.levelId,
    ) ?? { value: currentCourse.levelId, label: "" };
    return {
      name: currentCourse.name,
      code: currentCourse.code,
      codeIS: currentCourse.codeIS,
      status: optionsStatus.find((el) => el.value === currentCourse.status),
      level: {
        value: levelOption.value,
        label: levelOption.label ?? "",
      },
      certificateValidityPeriod: currentCourse.certificateValidityPeriod,
      replacementCourseIds: toSelectOptions(currentCourse.replacementCourses),
    };
  }, [openModal, currentCourse, levelSelectOptionsList]);

  const waitForLevelsInEdit =
    openModal === OpenModal.Edit &&
    currentCourse &&
    levelSelectOptionsList.length === 0;

  if (
    (getCoursesLoading && openModal !== OpenModal.Add) ||
    waitForLevelsInEdit ||
    levelSelectOptionsList.length === 0
  ) {
    return <Preloader />;
  }

  return (
    <CoursesManagement
      defaultValues={formInitialValues}
      levelSelectOptionsList={levelSelectOptionsList}
      {...props}
    />
  );
};
